<template lang="pug">
button.gtm-category.relative.w-full.flex.justify-center.overflow-hidden(type="button", :class="[enterAvailable ? 'cursor-pointer' : 'cursor-default', { active: enterAvailable && !jackpotDisabled }]", :disabled="jackpotDisabled || isFakeJackpot", :data-location="parent ? parent.category : ''", :data-icon="jackpot.category", @click="enter")
  .jackpot-room.flex.items-end(:data-game-entry="props.jackpot?.name?.['en-US']")
    .absolute.w-full
      template(v-if="adUrlPwa && !isFakeJackpot")
        .jackpot-bg.bg-cover.w-full.h-full.rounded-xl(:style="backgroundImageStyle")
      template(v-else)
        .jackpot-bg.bg-cover.w-full.h-full.rounded-xl(:class="[{ loop: isLoop }, { 'bg-disabled': isFakeJackpot }]")
    .jackpot-box(class="portrait:pb-2")
      .flex.flex-col.h-full.w-full.items-center
        jackpotCounter.jackpot-counter-text.w-full.jackpot-reward-bg.text-center.text-primary-text.font-black.font-mono(v-if="jackpot", :jackpot="jackpot", :isJackpotUnstart="isJackpotUnstart", class="sm:p-2")
        .flex.flex-1.flex-col.w-full
          template(v-if="games")
            template(v-if="games?.length > 0 && !isFakeJackpot")
              swiper-container.jackpot-game.text-primary-text.flex.justify-center.w-full(ref="bannerSwiper", events-prefix="swiper-", :key="swiperKey", :modules="[Autoplay]", :loop="canSlide", :autoplay-delay="5000", :autoplay-disable-on-interaction="false", :autoplay-pause-on-interaction="false", :autoplay-stop-on-interaction="false", :navigation="false", :pagination="false", :lazy="true", @click.stop="")
                swiper-slide.gtm-activity-item.flex.justify-center.w-full.swiper-no-swiping(v-for="item in games", :key="item.gameCode")
                  BaseGameCard.favorite-hide.frame-full(v-if="item", dimension="w-2/3 h-min", :key="item.gameCode", :game="item", :showMaintenance="false", :tagVisible="false", :nameVisible="false")
            .jackpot-game.text-primary-text.flex.justify-center(v-else)
              BaseGameCard.frame-full(v-if="fakeGame", dimension="w-2/3 h-min", :key="fakeGame.gameCode", :game="fakeGame", :isJackpotMysteryCard="true", :nameVisible="false")
          span.timer.flex.flex-1.align-text-bottom.w-full.h-fit.justify-center.items-center.text-primary-text.px-2.font-black.font-mono.truncate(v-if="!(isLoop && !jackpotDisabled)") {{ remainingTime === 'coming_soon' ? $t('description.coming_soon') : remainingTime }}
</template>

<script setup>
import padStart from 'lodash/padStart'
import { Autoplay } from 'swiper/modules'
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'

import { gameApi } from '@/api'
import { useLocaleSetting } from '@/composables/core/useLocaleSetting'

const { getCdnUrl } = useCdnAssets()
const { getUrl } = useLocaleSetting()

const props = defineProps({
  jackpot: {
    type: Object,
    required: true,
  },
  parent: {
    type: Object,
    default: () => ({}),
  },
  enterAvailable: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['enter', 'finish'])

const { $SoundControl, $analytics, $request, $duration } = useNuxtApp()

// Constants
const MAX_GAMES_DISPLAY = 6
const COUNTDOWN_INTERVAL_MS = 1000
const MAX_DAYS_DISPLAY = 99
const DEFAULT_TIME_FORMAT = '00d:00h:00m:00s'
const COMING_SOON_TEXT = 'coming_soon'
const FAKE_JACKPOT_ID = '0'

// Data
const timer = ref(null)
const remainingTime = ref(COMING_SOON_TEXT)
const jackpotDisabled = ref(false)
const isJackpotFinished = ref(false)
const games = ref(null)

// Computed
const fakeGame = computed(() => ({
  gameCode: FAKE_JACKPOT_ID,
  providerId: '',
  name: {
    'en-US': '',
  },
}))

const adUrlPwa = computed(() => {
  const localeUrl = getUrl(props.jackpot?.localeSetting, 'adUrlPwa') || props.jackpot?.adUrlPwa?.[0]?.url
  return getCdnUrl(localeUrl)
})

const backgroundImageStyle = computed(() => {
  return adUrlPwa.value ? { backgroundImage: `url(${adUrlPwa.value})` } : null
})

const isLoop = computed(() => {
  return props.jackpot.type === 'LOOP'
})

const isJackpotUnstart = computed(() => {
  return jackpotDisabled.value && !!timer.value
})

const isFakeJackpot = computed(() => {
  return props.jackpot.id === FAKE_JACKPOT_ID || !props.jackpot.id || isJackpotFinished.value
})

const swiperKey = computed(() => {
  return `swiper-${props.jackpot.id}`
})

// 只有多於一款遊戲時才啟用 Swiper loop：slide 數不足時 loop 會觸發 loopCreate
// 複製／搬移 DOM 節點，與 Vue 的 patch 產生衝突（CLIENT-PWA-SSR-C2/C3/C1，#322）
const canSlide = computed(() => (games.value?.length || 0) > 1)

// Methods
const clearTimer = () => {
  if (timer.value) {
    clearInterval(timer.value)
    timer.value = null
  }
}

const formatTimeRemaining = (diff) => {
  const duration = $duration(diff)
  const [hour, minute, second] = duration.format('HH:mm:ss').split(':')
  const day = padStart(Math.min(Math.floor(duration.asDays()), MAX_DAYS_DISPLAY), 2, '0')
  return isNaN(day) ? DEFAULT_TIME_FORMAT : `${day}d:${hour}h:${minute}m:${second}s`
}

const startUnstartedCountdown = () => {
  jackpotDisabled.value = true
  remainingTime.value = COMING_SOON_TEXT

  timer.value = setInterval(() => {
    const diff = props.jackpot.startTime - Date.now()
    if (diff <= 0) {
      clearTimer()
      jackpotDisabled.value = false
      countdown()
    }
  }, COUNTDOWN_INTERVAL_MS)
}

const startActiveCountdown = () => {
  jackpotDisabled.value = false
  const diff = props.jackpot.endTime - Date.now()
  remainingTime.value = formatTimeRemaining(diff)

  timer.value = setInterval(() => {
    const diff = props.jackpot?.endTime - Date.now()
    if (diff <= 0 || isNaN(diff)) {
      clearTimer()
      jackpotDisabled.value = true
      isJackpotFinished.value = true
      remainingTime.value = COMING_SOON_TEXT
    } else {
      remainingTime.value = formatTimeRemaining(diff)
    }
  }, COUNTDOWN_INTERVAL_MS)
}

const countdown = () => {
  clearTimer()
  const currentTime = Date.now()

  if (props.jackpot?.startTime > currentTime) {
    startUnstartedCountdown()
  } else if (props.jackpot?.endTime > currentTime) {
    startActiveCountdown()
  } else {
    isJackpotFinished.value = true
  }
}

const enter = () => {
  if (props.enterAvailable && !jackpotDisabled.value) {
    $SoundControl.sound.play('click')
    $analytics.push({
      event: 'lobby_click',
      location: props.parent?.name?.['en-US'],
      locationId: props.parent?.categoryId,
      icon: props.jackpot?.name?.['en-US'],
      iconId: props.jackpot.categoryId,
    })
    emit('enter', props.jackpot)
  }
}

const fetchJackpotGame = async () => {
  try {
    // 檢查 categoryId 是否存在
    if (!props.jackpot?.categoryId) {
      console.warn('[JackpotCard/fetchJackpotGame] categoryId is required but not provided')
      games.value = []
      return
    }

    const { appSecret } = useRuntimeConfig().public
    const gamesData = await $request.send(gameApi.getListCategory({ categoryId: props.jackpot.categoryId, index: 0, appSecret }))
    games.value = gamesData?.data ? [...gamesData.data].slice(0, MAX_GAMES_DISPLAY) : []
  } catch (error) {
    console.error('Failed to fetch jackpot games:', error)
    games.value = []
  }
}

// Watchers
watch(
  () => props.jackpot?.endTime,
  (newEndTime, oldEndTime) => {
    if (newEndTime && !timer.value && newEndTime > Date.now()) {
      countdown()
    }
  }
)

watch(isFakeJackpot, (newData, oldData) => {
  if (newData && !oldData) {
    games.value = null
    emit('finish', props.jackpot.jackpotId)
  } else if (!newData && oldData) {
    remainingTime.value = COMING_SOON_TEXT
    countdown()
    fetchJackpotGame()
  }
})

// Lifecycle
onMounted(() => {
  if (props.jackpot?.endTime > Date.now()) {
    countdown()
  } else if (isFakeJackpot.value) {
    remainingTime.value = COMING_SOON_TEXT
  } else {
    remainingTime.value = DEFAULT_TIME_FORMAT
  }

  if (props.jackpot.categoryId !== FAKE_JACKPOT_ID && props.jackpot.categoryId) {
    fetchJackpotGame()
  }
})

onBeforeUnmount(() => {
  clearTimer()
})
</script>

<style>
@reference "@/assets/css/tailwind.css";

.jackpot-room {
  height: 100%;
  width: 100%;
  max-height: 690px;
  max-width: 440px;
  aspect-ratio: 44 / 69;
  container-type: inline-size;
}

.jackpot-bg {
  background-image: url('assets/images/game/game_img_jackpot_normal.png');
  background-position: center;
  background-repeat: no-repeat;
  @apply portrait:w-full portrait:h-full;
  aspect-ratio: 44 / 69;
}

.jackpot-bg.loop {
  background-image: url('assets/images/game/game_img_jackpotloop_normal.png');
}

.jackpot-bg.bg-disabled {
  background-image: url('assets/images/game/game_img_jackpot_disable.png');
}

.jackpot-box {
  width: 100%;
  height: 65%;
  z-index: 1;
}

.jackpot-box .timer {
  @apply text-sm;
  padding-bottom: 1rem;
}

.room-name {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  white-space: normal;
  text-overflow: ellipsis;
  overflow: hidden;
}

.jackpot-reward-bg {
  background: linear-gradient(to right, transparent 0%, black 50%, transparent 100%);
  @apply flex items-center justify-center;
  min-height: 1.5rem;
}

.favorite-hide .favorite {
  display: none;
}

.frame-full .game-frame {
  width: 100%;
}

.jackpot-box .timer {
  padding-bottom: 1rem;
  /* Fallback: Original Logic */
  @apply text-2xs;
}

@media (min-width: 640px) {
  .jackpot-box .timer {
    padding-bottom: 1rem;
    @apply text-xs;
  }
}

@media (min-width: 768px) {
  .jackpot-box .timer {
    padding-bottom: 1.5rem;
    @apply text-sm;
  }
}

@media (min-width: 1366px) {
  .jackpot-box .timer {
    padding-bottom: 1rem;
    @apply text-lg;
  }
}

.jackpot-counter-text {
  /* Fallback: Original Logic */
  @apply text-xs sm:text-sm md:text-base 3xl:text-xl;
}

@supports (container-type: inline-size) {
  .jackpot-box .timer {
    padding-bottom: 1rem;
    font-size: 7.5cqw;
  }

  .jackpot-counter-text {
    font-size: 9cqw;
  }
}
</style>
